import {ClassComponent, Vnode} from 'mithril';
import app from 'flarum/forum/app';
import Model from 'flarum/common/Model';
import RequestError from 'flarum/common/utils/RequestError';
import FieldList from '../fields/FieldList';
import ValueHistory from '../utils/ValueHistory';
import SaveControls from '../components/SaveControls';
import parseValidationError from '../utils/parseValidationError';
import Uploader from '../utils/Uploader';
import Form from '../models/Form';

interface LinkedFormFillAttrs {
    form: Form
    linked: Model
    floating?: boolean
    className?: string
}

export default class LinkedFormFill implements ClassComponent<LinkedFormFillAttrs> {
    uploader!: Uploader
    history!: ValueHistory
    dirty: boolean = false
    loading: boolean = false
    savedAt: Date | null = null
    validationErrors: any = {}

    oninit(vnode: Vnode<LinkedFormFillAttrs, this>) {
        const {form, linked} = vnode.attrs;

        const submissions = linked.formulaireSubmissions(); // Use the relationship, that way we are certain all submissions in there are already for the correct linked resource
        const submission = submissions ? submissions.find(submission => {
            const thisForm = submission.form();

            return thisForm && thisForm.id() === form.id();
        }) : null;
        this.uploader = new Uploader(form);

        this.history = new ValueHistory(submission ? submission.formData() : {});
    }

    view(vnode: Vnode<LinkedFormFillAttrs, this>) {
        const {form, linked} = vnode.attrs;

        const list = new FieldList({
            configs: form.template()!,
            submission: this.history.value,
            uploader: this.uploader,
            onchange: () => {
                this.history.save();
                this.dirty = true;
            },
            disabled: this.loading || this.uploader.uploading,
            validationErrors: (this.validationErrors.formulaireForms || {})[form.id()!],
        });

        const inReadOnly = !form.attribute('canSubmit');

        return m('.FormulaireLinkedForm', {
            className: vnode.attrs.className,
        }, [
            list.listView(inReadOnly),
            m(SaveControls, {
                dirty: this.dirty,
                loading: this.loading,
                savedAt: this.savedAt,
                inError: Object.keys(this.validationErrors).length > 0,
                history: app.forum.attribute('formulaireShowHistoryControls') ? this.history : null,
                floating: typeof vnode.attrs.floating === 'undefined' ? true : vnode.attrs.floating,
                onsave: () => {
                    this.loading = true;

                    linked.save({
                        relationships: {
                            formulaireForms: [
                                {
                                    verbatim: true, // Flarum workaround, handled in patchStore
                                    type: 'formulaire-forms', // Not required for API, but required for save() which stores this info
                                    id: form.id(),
                                    attributes: {
                                        data: this.history.value,
                                    },
                                } as any,
                            ],
                        },
                    }, {
                        errorHandler: this.onerror.bind(this),
                    }).then(() => {
                        this.loading = false;
                        this.dirty = false;
                        this.savedAt = new Date();
                        this.validationErrors = {};
                        this.history.clear();
                        m.redraw();
                    }).catch(error => {
                        this.loading = false;
                        m.redraw();
                        throw error;
                    });
                },
            }),
        ]);
    }

    onerror(error: RequestError) {
        this.loading = false;

        let wasHandled = false;

        if (error.status === 422 && error.response?.errors) {
            wasHandled = true;

            this.validationErrors = parseValidationError(error);
        }

        m.redraw();

        if (!wasHandled) {
            throw error;
        }
    }
}
